import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Landmark, Copy, CheckCircle2, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const copy = (value) => {
  if (!navigator.clipboard) { toast.error('No se pudo copiar'); return; }
  navigator.clipboard.writeText(String(value || ''))
    .then(() => toast.success('Copiado'))
    .catch(() => toast.error('No se pudo copiar'));
};

/** Checkout propio (whitelabel): el pastor paga sin salir de la Suite. */
export function CauraCheckout({ order, onClose, onPaid }) {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ reference: '', amount: '', date: new Date().toISOString().slice(0, 10),
                                     phone: '', bankCode: '', docNumber: '' });
  const [debit, setDebit] = useState({ identificacion: '', banco: '', telefono: '', token: '' });
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [paid, setPaid] = useState(false);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    const res = await api.get(`/api/billing/orders/${order.order_id}`);
    setDetail(res.data.data);
    const status = (res.data.data?.status || res.data.data?.local_status || '').toLowerCase();
    if (['paid', 'completed', 'approved'].includes(status)) {
      setPaid(true);
      setVerifying(false);
      clearInterval(pollRef.current);
      onPaid?.();
    }
    return res.data.data;
  }, [order.order_id, onPaid]);

  useEffect(() => {
    refresh().then(d => setForm(f => ({ ...f, amount: String(d?.amount ?? order.amount_ves ?? '') })))
      .catch(() => toast.error('No se pudo cargar la orden'));
    return () => clearInterval(pollRef.current);
  }, [refresh, order.amount_ves]);

  const startPolling = () => {
    setVerifying(true);
    clearInterval(pollRef.current);
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      await refresh().catch(() => {});
      if (ticks >= 24) { clearInterval(pollRef.current); setVerifying(false); }
    }, 8000);
  };

  const submitMobile = async () => {
    setSending(true);
    try {
      await api.post(`/api/billing/orders/${order.order_id}/verify`, {
        payment_method: 'p2p',
        amount: form.amount,
        reference: form.reference,
        date: form.date,
        phoneCode: form.phone.slice(0, 4),
        phone: form.phone.slice(4),
        bankCode: form.bankCode,
        docType: 'V',
        docNumber: form.docNumber,
      });
      toast.success('Pago informado. Verificando con el banco…');
      startPolling();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo informar el pago');
    } finally {
      setSending(false);
    }
  };

  const requestToken = async () => {
    setSending(true);
    try {
      await api.post(`/api/billing/orders/${order.order_id}/debit/request-token`, {
        identificacion: debit.identificacion, banco: debit.banco, telefono: debit.telefono,
      });
      toast.success('Te llegó un código por SMS (vence en 2 minutos)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo solicitar el código');
    } finally {
      setSending(false);
    }
  };

  const executeDebit = async () => {
    setSending(true);
    try {
      await api.post(`/api/billing/orders/${order.order_id}/debit/execute`, { token: debit.token });
      toast.success('Débito enviado. Verificando…');
      startPolling();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo completar el débito');
    } finally {
      setSending(false);
    }
  };

  const bank = detail?.merchant_bank || {};

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="caura-checkout">
        <DialogHeader>
          <DialogTitle className="text-base md:text-lg">
            Pagar {order.amount_usd?.toFixed(2)} USD
            {order.amount_ves ? ` · ${Number(order.amount_ves).toLocaleString('es-VE')} Bs.` : ''}
          </DialogTitle>
        </DialogHeader>

        {paid ? (
          <div className="py-8 text-center space-y-2" data-testid="checkout-paid">
            <CheckCircle2 className="w-10 h-10 text-[#1F9D7A] mx-auto" />
            <p className="font-medium">Pago confirmado</p>
            <p className="text-sm text-muted-foreground">La suscripción quedó al día.</p>
            <Button onClick={onClose} className="mt-2">Listo</Button>
          </div>
        ) : !detail ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <Tabs defaultValue="p2p">
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="p2p" data-testid="tab-p2p">
                <Smartphone className="w-3.5 h-3.5 mr-1" /> Pago Móvil
              </TabsTrigger>
              <TabsTrigger value="debit" data-testid="tab-debit">
                <Landmark className="w-3.5 h-3.5 mr-1" /> Débito inmediato
              </TabsTrigger>
            </TabsList>

            <TabsContent value="p2p" className="space-y-3 pt-3">
              <div className="rounded-lg border p-3 space-y-1 text-sm bg-muted/40">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                  Haz el pago móvil a:
                </p>
                {[['Banco', bank.banco_receptor || bank.bank || bank.bank_name],
                  ['Teléfono', bank.telefono_cobrador || bank.phone],
                  ['RIF', bank.rif || bank.document],
                  ['Cuenta', bank.cuenta_receptora || bank.account],
                  ['Titular', bank.titular || bank.company_name]]
                  .filter(([, v]) => v).map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">{label}</span>
                    <button className="font-medium flex items-center gap-1 hover:text-[#056A84]"
                            onClick={() => copy(value)} data-testid={`copy-${label.toLowerCase()}`}>
                      {value} <Copy className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Referencia</Label>
                  <Input value={form.reference} placeholder="Últimos dígitos"
                         onChange={e => setForm(f => ({ ...f, reference: e.target.value }))}
                         data-testid="checkout-reference" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Monto pagado (Bs.)</Label>
                  <Input value={form.amount}
                         onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                         data-testid="checkout-amount" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Fecha</Label>
                  <Input type="date" value={form.date}
                         onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                         data-testid="checkout-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Teléfono emisor</Label>
                  <Input value={form.phone} placeholder="04141234567"
                         onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                         data-testid="checkout-phone" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Código del banco</Label>
                  <Input value={form.bankCode} placeholder="0105"
                         onChange={e => setForm(f => ({ ...f, bankCode: e.target.value }))}
                         data-testid="checkout-bank" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Cédula del emisor</Label>
                  <Input value={form.docNumber} placeholder="12345678"
                         onChange={e => setForm(f => ({ ...f, docNumber: e.target.value }))}
                         data-testid="checkout-doc" />
                </div>
              </div>

              <Button onClick={submitMobile} disabled={sending || verifying || !form.reference}
                      className="w-full bg-[#056A84] hover:bg-[#045A70] text-white"
                      data-testid="checkout-submit">
                {sending || verifying
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />
                      {verifying ? 'Verificando con el banco…' : 'Enviando…'}</>
                  : 'Ya pagué, verificar'}
              </Button>
              {verifying && (
                <p className="text-xs text-muted-foreground text-center" data-testid="checkout-verifying">
                  La verificación puede tardar entre 1 y 3 minutos. Puedes cerrar esta ventana:
                  la factura se marca pagada sola cuando el banco confirme.
                </p>
              )}
            </TabsContent>

            <TabsContent value="debit" className="space-y-3 pt-3">
              <p className="text-sm text-muted-foreground">
                Autoriza el cobro desde tu cuenta con un código que llega por SMS.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Cédula</Label>
                  <Input value={debit.identificacion}
                         onChange={e => setDebit(d => ({ ...d, identificacion: e.target.value }))}
                         data-testid="debit-doc" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Código del banco</Label>
                  <Input value={debit.banco} placeholder="0105"
                         onChange={e => setDebit(d => ({ ...d, banco: e.target.value }))}
                         data-testid="debit-bank" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">Teléfono afiliado</Label>
                  <Input value={debit.telefono} placeholder="04141234567"
                         onChange={e => setDebit(d => ({ ...d, telefono: e.target.value }))}
                         data-testid="debit-phone" />
                </div>
              </div>
              <Button variant="outline" onClick={requestToken} disabled={sending}
                      className="w-full" data-testid="debit-request-token">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviarme el código'}
              </Button>
              <div className="space-y-1.5">
                <Label className="text-xs">Código recibido (8 dígitos)</Label>
                <Input value={debit.token} maxLength={8}
                       onChange={e => setDebit(d => ({ ...d, token: e.target.value }))}
                       data-testid="debit-token" />
              </div>
              <Button onClick={executeDebit} disabled={sending || debit.token.length < 8}
                      className="w-full bg-[#056A84] hover:bg-[#045A70] text-white"
                      data-testid="debit-execute">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar débito'}
              </Button>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CauraCheckout;
